import { useImperativeHandle, useState } from "react";
import type { JSX, Ref } from "react";

import { componentManager, rwc } from "@frontend/bridge/ComponentManager";

import { Meter } from "./Meter";
import { type MeterDataProps, meterDataSchema } from "./Meter.schema";
import { MeterIndicator } from "./MeterIndicator";
import { MeterLabel } from "./MeterLabel";
import { MeterTrack } from "./MeterTrack";
import { MeterValue } from "./MeterValue";
import { METER_TAG_NAME } from "./constants";

export interface MeterHandle {
  value: (value: number) => void;
}

declare module "@frontend/bridge/types" {
  interface ComponentRegistryMap {
    [METER_TAG_NAME]: { props: MeterDataProps; handle: MeterHandle };
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "rwc-meter": HTMLElement & MeterHandle;
  }
}

export function RWCMeter({
  ref,
  label,
  value: initialValue,
  min,
  max,
}: MeterDataProps & { ref?: Ref<MeterHandle> }): JSX.Element {
  const [value, setValue] = useState(initialValue);

  useImperativeHandle(ref, () => ({ value: setValue }), []);

  return (
    <Meter value={value} min={min} max={max}>
      {label ? <MeterLabel>{label}</MeterLabel> : null}
      <MeterValue />
      <MeterTrack>
        <MeterIndicator />
      </MeterTrack>
    </Meter>
  );
}

componentManager.register(
  rwc(METER_TAG_NAME, {
    component: RWCMeter,
    dataSchema: meterDataSchema,
  }),
);
